"use client"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import { AnimatedNumber, NumberDisplay } from "@opensea/ui-kit"
import type { Nullable } from "@opensea/utils"
import {
  type CurrencyIdentifier,
  getCurrencyIdentifier,
} from "../currencies/utils"
import type { Conversion } from "./types"
import { UsdPriceFragment } from "./UsdPrice.fragment"
import { useUsdPriceValue } from "./use-usd-price"

type Props = {
  price: Nullable<FragmentOf<typeof UsdPriceFragment>>
  multiplier?: number
  conversion?: Conversion
  animated?: boolean
  display?: "usd" | "usd-compact" | "usd-full"
  custom?: Intl.NumberFormatOptions
}

type UsdPriceDisplayProps = {
  currency: CurrencyIdentifier
  multiplier?: number
  unitPrice?: number
  conversion?: Conversion
  usdPrice?: number
  animated?: boolean
  display?: "usd" | "usd-compact" | "usd-full"
  custom?: Intl.NumberFormatOptions
}

export function UsdPriceDisplay({
  conversion = "current",
  currency,
  unitPrice,
  usdPrice: usdPriceProp,
  multiplier = 1,
  animated,
  display = "usd",
  custom,
}: UsdPriceDisplayProps) {
  const NumberComponent = animated ? AnimatedNumber : NumberDisplay

  const usdPriceValue = useUsdPriceValue({
    currency,
    unitPrice,
    usdPrice: usdPriceProp,
    conversion,
    multiplier,
  })

  return (
    <NumberComponent custom={custom} display={display} value={usdPriceValue} />
  )
}

export function UsdPrice({
  price: priceKey,
  conversion,
  multiplier = 1,
  animated,
  display = "usd",
  custom,
}: Props) {
  const price = readFragment(UsdPriceFragment, priceKey)

  const NumberComponent = animated ? AnimatedNumber : NumberDisplay

  if (!price) {
    return <NumberComponent display="usd" value={null} />
  }

  const tokenCurrencyIdentifier = getCurrencyIdentifier(price.token)
  return (
    <UsdPriceDisplay
      conversion={conversion}
      currency={tokenCurrencyIdentifier}
      custom={custom}
      display={display}
      multiplier={multiplier}
      unitPrice={price.token.unit}
      usdPrice={price.usd}
    />
  )
}
